import { cn } from "@/lib/utils";

/**
 * The About page rhythm: one gutter, one vertical padding, one shell width, and
 * one section heading. Every section goes through this so the cadence cannot
 * drift section by section.
 */
export const ABOUT_GUTTER = "px-4 md:px-8";
export const ABOUT_SHELL = "mx-auto w-full max-w-[1280px]";

// Concatenated rather than passed through `cn`: tailwind-merge does not parse
// the trailing `!`, and the marketing theme styles bare h2s with a higher
// specificity than utilities, so these need it.
const SECTION_TITLE =
  "text-marketing-text text-[28px]! leading-[1.08]! font-medium! tracking-[-0.03em]! text-balance sm:text-[36px]! lg:text-[42px]!";

export function SectionTitle({
  as: Tag = "h2",
  className,
  children,
}: {
  as?: "h1" | "h2";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Tag className={`${SECTION_TITLE} ${className ?? ""}`}>{children}</Tag>
  );
}

export function SectionHead({
  title,
  intro,
  as,
  className,
}: {
  title: React.ReactNode;
  intro?: React.ReactNode;
  as?: "h1" | "h2";
  className?: string;
}) {
  return (
    <div className={className}>
      <SectionTitle as={as}>{title}</SectionTitle>
      {intro ? (
        <p className="text-marketing-text-muted mt-4 max-w-[52ch] text-[16px] leading-[1.6] text-pretty">
          {intro}
        </p>
      ) : null}
    </div>
  );
}

export function AboutSection({
  id,
  className,
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className={cn(
        ABOUT_GUTTER,
        "scroll-mt-24 py-12 sm:py-20 lg:py-24",
        className,
      )}
    >
      <div className={ABOUT_SHELL}>{children}</div>
    </section>
  );
}

/** Heading on the left, content on the right, from lg up. */
export function TwoColumn({
  title,
  intro,
  children,
}: {
  title: React.ReactNode;
  intro?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
      <SectionHead title={title} intro={intro} className="lg:col-span-5" />
      <div className="lg:col-span-7">{children}</div>
    </div>
  );
}

export const bodyCopy =
  "text-marketing-text-muted text-[17px] leading-[1.6] text-pretty";

export const mutedPanel = "bg-muted/60 rounded-[20px]";
